import {
  classifyError,
  type ErrorKind,
  errorGuidanceFa,
  errorMessageFa,
  isApiError,
  SESSION_RATE_LIMITED_FA,
} from '@saay/api-client';
import { formatCount } from '@saay/formatting';
import { type ReactNode } from 'react';

import type { IconName } from './icon-paths.js';
import { Icon } from './icons.js';
import { copyableRequestId } from './request-id-policy.js';
import { Button, Ltr, Skeleton } from './primitives.js';
import { useOptionalToast } from './toast.js';

function PanelIcon({ name }: { readonly name: IconName }) {
  return (
    <span aria-hidden="true" className="saay-panel__icon">
      <Icon name={name} />
    </span>
  );
}

/**
 * FTD §11 state-matrix components. `DataState` picks the state of a query screen; each state also
 * exists on its own for screens that need it directly.
 */

export function EmptyState({
  action,
  description,
  icon = 'doc',
  title,
}: {
  readonly action?: ReactNode;
  readonly description?: ReactNode;
  /** A prototype icon for the empty state (default: a document). */
  readonly icon?: IconName;
  readonly title: ReactNode;
}) {
  return (
    <section className="saay-panel" aria-live="polite">
      <PanelIcon name={icon} />
      <h2 className="saay-panel__title">{title}</h2>
      {description === undefined ? null : <p className="saay-panel__text">{description}</p>}
      {action}
    </section>
  );
}

/** The copyable server request ID (`<bdi dir="ltr">`), for support and unresolved errors. */
export function RequestId({ value }: { readonly value: string }) {
  const toast = useOptionalToast();
  return (
    <span className="saay-request-id">
      <span>کد پیگیری:</span>
      <Ltr>
        <code>{value}</code>
      </Ltr>
      <Button
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => {
            toast?.notify({ title: 'کپی شد' });
          });
        }}
        size="sm"
        variant="ghost"
      >
        کپی
      </Button>
    </span>
  );
}

export interface ErrorStateProps {
  readonly error: unknown;
  readonly onRetry?: () => void;
  /** `false` when another control already counts the `Retry-After` wait down. */
  readonly showRetryAfter?: boolean;
  readonly title?: ReactNode;
}

/**
 * An error from the API envelope: the server `messageFa`, the `requestId`, and retry when the error
 * is retryable (FTD §11). The UI never branches on the message text.
 */
export function ErrorState({
  error,
  onRetry,
  showRetryAfter = true,
  title = 'مشکلی پیش آمد',
}: ErrorStateProps) {
  const requestId = copyableRequestId(error);
  const kind = classifyError(error);
  const retryable =
    kind === 'offline' ||
    kind === 'unavailable' ||
    kind === 'rate-limited' ||
    (isApiError(error) && error.retryable);
  const retryAfter = isApiError(error) ? error.retryAfterSeconds : undefined;
  const guidance = errorGuidanceFa(error);

  return (
    <section className="saay-panel" data-tone="danger" role="alert">
      <PanelIcon name="alert" />
      <h2 className="saay-panel__title">{title}</h2>
      <p className="saay-panel__text">{errorMessageFa(error)}</p>
      {guidance === undefined ? null : <p className="saay-panel__text">{guidance}</p>}
      {retryAfter === undefined || !showRetryAfter ? null : (
        <p className="saay-panel__text">{`حدود ${formatCount(retryAfter)} ثانیه دیگر دوباره تلاش کنید.`}</p>
      )}
      {requestId === null || requestId === '' ? null : <RequestId value={requestId} />}
      {retryable && onRetry !== undefined ? (
        <Button onClick={onRetry} variant="secondary">
          تلاش دوباره
        </Button>
      ) : null}
    </section>
  );
}

/**
 * A `429` on the session self-context (AC §7.5): the rejected-authentication IP limit or a per-user
 * limit. It shows the wait, a retry, and a fresh login (a stale token is the usual cause).
 */
export function SessionRateLimited({
  error,
  onLogin,
  onRetry,
}: {
  readonly error: unknown;
  readonly onLogin: () => void;
  readonly onRetry?: () => void;
}) {
  const retryAfter = isApiError(error) ? error.retryAfterSeconds : undefined;
  const requestId = copyableRequestId(error);
  return (
    <section className="saay-panel" data-tone="warning" role="alert">
      <PanelIcon name="clock" />
      <h2 className="saay-panel__title">{SESSION_RATE_LIMITED_FA.title}</h2>
      <p className="saay-panel__text">{SESSION_RATE_LIMITED_FA.text}</p>
      {retryAfter === undefined ? null : (
        <p className="saay-panel__text">{`حدود ${formatCount(retryAfter)} ثانیه دیگر دوباره تلاش کنید.`}</p>
      )}
      {requestId === null || requestId === '' ? null : <RequestId value={requestId} />}
      <div className="saay-actions">
        {onRetry === undefined ? null : (
          <Button onClick={onRetry} variant="secondary">
            تلاش دوباره
          </Button>
        )}
        <Button onClick={onLogin} variant="ghost">
          ورود دوباره
        </Button>
      </div>
    </section>
  );
}

/** 403: access denied, without any detail about the resource (FTD §11). */
export function AccessDenied() {
  return (
    <section className="saay-panel" data-tone="warning" role="alert">
      <PanelIcon name="shield" />
      <h2 className="saay-panel__title">به این بخش دسترسی ندارید</h2>
      <p className="saay-panel__text">
        نقش شما اجازه دیدن این بخش را ندارد. اگر فکر می‌کنید اشتباه است، با مدیر خود تماس بگیرید.
      </p>
    </section>
  );
}

/** 401 after a failed refresh: re-login returns to the same route (FTD §11). */
export function SessionExpired({ onLogin }: { readonly onLogin: () => void }) {
  return (
    <section className="saay-panel" role="alert">
      <PanelIcon name="clock" />
      <h2 className="saay-panel__title">برای امنیت حساب، دوباره وارد شوید</h2>
      <p className="saay-panel__text">پس از ورود به همین صفحه برمی‌گردید.</p>
      <Button onClick={onLogin}>ورود دوباره</Button>
    </section>
  );
}

export interface DataStateProps {
  readonly children: ReactNode;
  readonly empty?: ReactNode;
  readonly error: unknown;
  readonly isEmpty?: boolean;
  readonly onLogin: () => void;
  readonly onRetry?: () => void;
  /**
   * The query is the session self-context (`/admin/me`, `/merchant/me`): a `429` there shows
   * {@link SessionRateLimited} (AC §7.5 rejected-auth limit) instead of the generic error.
   */
  readonly session?: boolean;
  readonly skeleton?: ReactNode;
  readonly status: 'pending' | 'error' | 'success';
}

/**
 * Maps a query to its FTD §11 state: loading skeleton, error with request ID, unauthorized,
 * session expired, empty, or the content.
 */
export function DataState({
  children,
  empty,
  error,
  isEmpty = false,
  onLogin,
  onRetry,
  session = false,
  skeleton,
  status,
}: DataStateProps) {
  if (status === 'pending') {
    return <>{skeleton ?? <Skeleton />}</>;
  }
  if (status === 'error') {
    const kind: ErrorKind = classifyError(error);
    if (kind === 'unauthorized') {
      return <AccessDenied />;
    }
    if (kind === 'session-expired') {
      return <SessionExpired onLogin={onLogin} />;
    }
    if (session && isApiError(error) && error.status === 429) {
      return (
        <SessionRateLimited
          error={error}
          onLogin={onLogin}
          {...(onRetry === undefined ? {} : { onRetry })}
        />
      );
    }
    return <ErrorState error={error} {...(onRetry === undefined ? {} : { onRetry })} />;
  }
  if (isEmpty) {
    return <>{empty ?? <EmptyState title="موردی برای نمایش نیست" />}</>;
  }
  return <>{children}</>;
}

/** A Phase 1 destination that has no screen yet: marked «به‌زودی» (no stage wording, D-48). */
export function ComingSoon({ title }: { readonly title: ReactNode }) {
  return (
    <EmptyState
      description="این بخش به‌زودی در دسترس قرار می‌گیرد."
      icon="clock"
      title={
        <>
          {title} <span className="saay-soon">به‌زودی</span>
        </>
      }
    />
  );
}
